import { useCallback, useEffect, useState } from "react";
import { CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, InputAdornment, TextField } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SearchIcon from "@mui/icons-material/Search";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { SoftButton, cls, useDialogPaperSx } from "../hatim/soft/SoftKit";
import { useCreateAccessRequestMutation, useGetPackageMutation, useListPackagesQuery } from "../../api/libraryApi";
import { lockReasonKey } from "./questionRules";

const TABS = [
  ["mine", "mzq_tab_mine"],
  ["starred", "mzq_tab_starred"],
  ["explore", "mzq_tab_explore"],
];

/** Kütüphane sayfası: her sayfa kendi sorgusuyla, "Daha fazla" ile eklenir. */
function PackagePage({ tab, q, page, onHasMore, renderRow }) {
  const { data, isFetching, isError } = useListPackagesQuery({
    tab,
    q,
    sort: tab === "explore" ? "stars" : "new",
    page,
  });
  const packages = data?.data?.packages || [];
  const hasMore = Boolean(data?.data?.hasMore);
  useEffect(() => {
    if (data) onHasMore(page, hasMore);
  }, [data, hasMore, page, onHasMore]);
  if (!data && isFetching) {
    return (
      <li className="py-6 flex justify-center">
        <CircularProgress size={22} />
      </li>
    );
  }
  if (isError && page === 1) return <PageMessage textKey="mzq_packages_failed" danger />;
  if (page === 1 && packages.length === 0) return <PageMessage textKey="mzq_no_packages" />;
  return packages.map(renderRow);
}

function PageMessage({ textKey, danger }) {
  const { t } = useScriptMode();
  return <li className={`py-6 text-sm text-center ${danger ? cls.danger : cls.muted}`}>{t(textKey)}</li>;
}

/**
 * Müzakereye paket seçimi (mobil PackagePickerModal): Paketlerim,
 * Yıldızlılarım ya da Keşfet, arama ile. Kilitli paket kilitli görünür,
 * seçilemez; izin istenebilir. Seçilince paket soruları ile onPick'e gider.
 */
export default function PackagePickerDialog({ open, onClose, onPick }) {
  const { t } = useScriptMode();
  const paperSx = useDialogPaperSx();
  const [tab, setTab] = useState("mine");
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [pageCount, setPageCount] = useState(1);
  const [hasMoreByPage, setHasMoreByPage] = useState({});
  const [picking, setPicking] = useState(null);
  const [error, setError] = useState(null);
  // Sunucu açılışta kilitli dediyse ya da istek gönderildiyse satır güncellenir.
  const [patched, setPatched] = useState({});
  const [getPackage] = useGetPackageMutation();
  const [requestAccess, { isLoading: requesting }] = useCreateAccessRequestMutation();

  useEffect(() => {
    const handle = setTimeout(() => setQ(search.trim()), 350);
    return () => clearTimeout(handle);
  }, [search]);
  useEffect(() => {
    setPageCount(1);
    setHasMoreByPage({});
  }, [tab, q, open]);
  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const reportHasMore = useCallback(
    (page, more) => setHasMoreByPage((prev) => (prev[page] === more ? prev : { ...prev, [page]: more })),
    []
  );

  const pick = async (id) => {
    if (picking) return;
    setPicking(id);
    setError(null);
    const result = await getPackage({ packageId: id });
    setPicking(null);
    if (result.error) {
      setError(result.error.message || t("mzq_package_failed"));
      return;
    }
    const pkg = result.data?.data?.package;
    if (!pkg) {
      setError(t("mzq_package_failed"));
      return;
    }
    // Liste açıkken kilitlenmiş olabilir: seçilmez, satır güncellenir.
    if (pkg.locked) {
      setPatched((prev) => ({ ...prev, [id]: pkg }));
      return;
    }
    onPick(pkg);
  };

  const request = async (pkg) => {
    const result = await requestAccess({ packageId: pkg.id });
    if (result.error) {
      setError(result.error.message || t("mzq_request_failed"));
      return;
    }
    setPatched((prev) => ({
      ...prev,
      [pkg.id]: { ...pkg, accessRequest: { id: result.data?.data?.request?.id, status: "pending" } },
    }));
  };

  const renderRow = (raw) => {
    const p = patched[raw.id] ? { ...raw, ...patched[raw.id] } : raw;
    const meta = [p.isMine ? null : p.ownerName, `${p.questionCount ?? 0} ${t("assess_question_word")}`]
      .filter(Boolean)
      .join(" · ");
    if (p.locked) {
      const pending = p.accessRequest?.status === "pending";
      return (
        <li key={p.id} className={`flex items-center gap-3 py-3 ${cls.row}`}>
          <LockOutlinedIcon fontSize="small" className={cls.muted} />
          <div className="min-w-0 flex-1">
            <div className={`text-sm truncate ${cls.title}`}>{p.title}</div>
            {p.ownerName ? <div className={`text-xs truncate ${cls.muted}`}>{p.ownerName}</div> : null}
            <div className={`text-xs ${cls.muted}`}>{t(lockReasonKey(p))}</div>
          </div>
          <SoftButton
            tone="soft"
            size="small"
            disabled={pending || requesting}
            onClick={() => request(p)}
            sx={{ py: 0.4, px: 1.5, fontSize: 13 }}
          >
            {t(pending ? "mzq_access_pending" : "mzq_request_access")}
          </SoftButton>
        </li>
      );
    }
    return (
      <li key={p.id}>
        <button
          type="button"
          onClick={() => pick(p.id)}
          disabled={!!picking}
          className={`w-full flex items-center gap-3 py-3 text-left ${cls.row} ${cls.rowHover}`}
        >
          <div className="min-w-0 flex-1">
            <div className={`text-sm line-clamp-2 ${cls.title}`}>{p.title}</div>
            <div className={`text-xs truncate ${cls.muted}`}>{meta}</div>
          </div>
          {picking === p.id ? (
            <CircularProgress size={18} />
          ) : (
            <ChevronRightIcon fontSize="small" className={cls.muted} />
          )}
        </button>
      </li>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: 1500 }}
      PaperProps={{ sx: { borderRadius: 3, height: "min(640px, 90vh)", ...paperSx } }}
    >
      <DialogTitle sx={{ fontWeight: 500, display: "flex", alignItems: "center", gap: 1 }}>
        <span className="flex-1">{t("mzq_pick_package")}</span>
        <IconButton size="small" onClick={onClose} aria-label={t("muzakere_cancel")}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <div className="flex gap-1 border-b border-terakkiBorder dark:border-white/[0.14]" role="tablist">
          {TABS.map(([value, key]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={tab === value}
              onClick={() => setTab(value)}
              className={`px-3 py-2 text-sm -mb-px border-b-2 ${
                tab === value
                  ? "border-terakkiPrimaryDark text-terakkiPrimaryDark dark:border-[#8FC9CA] dark:text-[#8FC9CA]"
                  : `border-transparent ${cls.muted}`
              }`}
            >
              {t(key)}
            </button>
          ))}
        </div>
        <TextField
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("mzq_search_package")}
          inputProps={{ "aria-label": t("mzq_search_package"), maxLength: 60 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" className={cls.muted} />
              </InputAdornment>
            ),
          }}
        />
        {error ? <p className={`text-sm ${cls.danger}`}>{error}</p> : null}
        <ul className="flex-1 min-h-0">
          {open
            ? Array.from({ length: pageCount }, (_, i) => (
                <PackagePage
                  key={`${tab}-${q}-${i + 1}`}
                  tab={tab}
                  q={q}
                  page={i + 1}
                  onHasMore={reportHasMore}
                  renderRow={renderRow}
                />
              ))
            : null}
        </ul>
        {hasMoreByPage[pageCount] ? (
          <div className="flex justify-center">
            <SoftButton tone="soft" onClick={() => setPageCount((n) => n + 1)}>
              {t("Daha fazla")}
            </SoftButton>
          </div>
        ) : null}
        {/* Kütüphane sayfası yeni sekmede: sihirbazdaki yarım iş kaybolmaz. */}
        <a
          href="#/uygulama/kutuphane"
          target="_blank"
          rel="noopener noreferrer"
          className={`self-center flex items-center gap-1 text-sm ${cls.accent} hover:underline`}
        >
          <OpenInNewIcon sx={{ fontSize: 15 }} aria-hidden />
          {t("lib_open_library")}
        </a>
      </DialogContent>
    </Dialog>
  );
}
